import { Button, Tag } from 'antd';
import { MapPinOff } from 'lucide-react';
import { useZoneMapEditor, type ZoneMapValue } from './use-zone-map-editor';

interface ZoneMapEditorProps {
  /** Real MapTiler tiles, or null when this deployment hasn't set MAPTILER_API_KEY. */
  tileKey: string | null;
  value: ZoneMapValue;
  onChange: (value: ZoneMapValue) => void;
}

/**
 * Draws a zone's polygon and/or centroid on a real map — optional, independent, and both null by
 * default. Fills its parent's height (ZoneForm sizes it); useZoneMapEditor's ResizeObserver keeps
 * MapLibre's canvas in step when that changes.
 */
export function ZoneMapEditor({ tileKey, value, onChange }: ZoneMapEditorProps) {
  const { containerRef, isMapReady, drawMode, setDrawMode, clearPolygon, clearCentroid } = useZoneMapEditor({ tileKey, value, onChange });

  if (!tileKey) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-line bg-page px-4 py-10 text-center">
        <MapPinOff className="size-5 text-muted" />
        <p className="max-w-sm text-sm text-muted">
          Map key not configured — this deployment hasn't set a MapTiler key. The zone can still be saved without a polygon or centroid.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Button size="small" type={drawMode === 'polygon' ? 'primary' : 'default'} onClick={() => setDrawMode('polygon')} disabled={!isMapReady}>
            Draw polygon
          </Button>
          <Button
            size="small"
            type={drawMode === 'point' ? 'primary' : 'default'}
            onClick={() => setDrawMode('point')}
            disabled={!isMapReady || !!value.polygon}
          >
            Set point only
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Tag color={value.polygon ? 'success' : 'default'}>{value.polygon ? 'Polygon set' : 'No polygon'}</Tag>
          <Tag color={value.centroid ? 'success' : 'default'}>{value.centroid ? 'Centroid set' : 'No centroid'}</Tag>
        </div>
      </div>
      <div ref={containerRef} className="min-h-0 w-full flex-1 rounded-lg border border-line" />
      <div className="flex gap-2">
        <Button size="small" disabled={!value.polygon} onClick={clearPolygon}>
          Clear polygon
        </Button>
        <Button size="small" disabled={!value.centroid} onClick={clearCentroid}>
          Clear centroid
        </Button>
      </div>
    </div>
  );
}
